import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PlugZap, ShieldCheck, RefreshCw, ExternalLink, CheckCircle2 } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";

export default function ConnectPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [params] = useSearchParams();
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/hubspot/status");
      setStatus(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const connect = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get("/hubspot/authorize");
      window.location.href = data.url;
    } catch (e) {
      setBusy(false);
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const err_qs = params.get("error");

  return (
    <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="connect-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Step 1</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
          Connect your HubSpot
        </h1>
        <p className="mt-3 text-slate-500 max-w-xl">
          We use HubSpot&apos;s read-only OAuth scopes. Your access &amp; refresh tokens are encrypted
          at rest with AES-GCM. You can disconnect any time from Settings.
        </p>
      </header>

      {err_qs && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="connect-oauth-error">
          HubSpot returned an error: <strong>{err_qs}</strong>
        </div>
      )}

      {status?.connected ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-4" data-testid="connect-connected">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">HubSpot is connected</div>
            <div className="text-sm text-slate-600 mt-1">
              Portal ID: <span className="font-mono">{status.portal_id}</span>
              {status.connected_at && (
                <> · connected {new Date(status.connected_at).toLocaleString()}</>
              )}
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => nav("/dashboard/mapping")}
                data-testid="go-to-mapping-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                Continue to stage mapping
              </button>
              <button
                onClick={() => nav("/dashboard/health")}
                data-testid="go-to-health-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 text-slate-900 px-4 py-2 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                See data health
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center" data-testid="connect-empty-state">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
            <PlugZap className="h-6 w-6" />
          </div>
          <h2 className="mt-6 text-xl font-semibold text-slate-900">
            No CRM connected yet
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm">
            Click below to authorize Recrysta with your HubSpot account. We&apos;ll immediately
            run an initial sync of your pipelines, owners, and deals.
          </p>
          <button
            onClick={connect}
            disabled={busy}
            data-testid="connect-hubspot-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
          >
            <PlugZap className="h-4 w-4" />
            {busy ? "Redirecting…" : "Connect HubSpot"}
          </button>
          {err && <div className="mt-4 text-sm text-red-600">{err}</div>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <ShieldCheck className="h-5 w-5 text-slate-500" />
          <div className="mt-3 font-semibold text-slate-900">Encrypted at rest</div>
          <div className="mt-1 text-sm text-slate-500">
            Access &amp; refresh tokens stored with AES-256-GCM.
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <RefreshCw className="h-5 w-5 text-slate-500" />
          <div className="mt-3 font-semibold text-slate-900">Automatic refresh</div>
          <div className="mt-1 text-sm text-slate-500">
            Access tokens are refreshed automatically as they expire.
          </div>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex items-center gap-1">
        Learn about HubSpot OAuth scopes
        <a
          href="https://developers.hubspot.com/docs/api/working-with-oauth"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
        >
          here <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
